import { getRandomItem } from ".";
/**
 * 将数字转为百分数
 * @param num
 * @returns
 */
export function formatPercentNumber(num: number): string {
    return isNaN(num) ? '-' : (num * 100).toFixed(2) + '%';
}

/**
 * 生成随机数
 * @param length 随机数长度
 * @returns
 */
export function createRandomNumber(length: number = 8) {
    return Math.floor(Math.random() * Math.pow(10, length));
}

/**
 * 保留几位小数
 */
export function roundNumber(number: number, digit = 2) {
    return Number(number.toFixed(digit));
}

/**
 * 添加正负号
 */
export function addSign(number: number): string {
    const str = String(number);
    return (number >= 0 ? '+' : '') + str;
}

export function findClosestNumber(list: number[], target: number): [number, number] {
    let closestNumber = list[0];
    let closestIndex = 0;
    let minDiff = Math.abs(list[0] - target);

    for (let i = 1; i < list.length; i++) {
        const currentDiff = Math.abs(list[i] - target);
        if (currentDiff < minDiff) {
            minDiff = currentDiff;
            closestNumber = list[i];
            closestIndex = i
        }
    }

    return [closestIndex, closestNumber];
}

export function getIndexOfMaxValue(arr: number[]) {
    if (!Array.isArray(arr) || arr.length === 0) {
        return -1; // 如果输入无效,返回-1
    }

    let maxValue = arr[0];
    let maxIndex = 0;

    for (let i = 1; i < arr.length; i++) {
        if (arr[i] > maxValue) {
            maxValue = arr[i];
            maxIndex = i;
        }
    }

    return maxIndex;
}

/**
 * 在范围内生成随机数
 * @param min 
 * @param max 
 * @returns 
 */
export function createRandomInRange(min: number, max: number) {
    return Math.random() * (max - min) + min;
}

/**
 * 取得落在圆环中的点的位置
 * @param r1 
 * @param r2 
 * @returns 
 */
export function createRandomPointInAnnulus(r1: number, r2: number): [number, number] {
    // 计算圆环的面积
    const innerArea = Math.PI * r1 * r1;
    const outerArea = Math.PI * r2 * r2;
    const annulusArea = outerArea - innerArea;
  
    // 生成一个 [0, annulusArea) 之间的随机数,表示点落在圆环内的概率
    const randomArea = Math.random() * annulusArea;
  
    // 根据随机面积计算点的半径
    const radius = Math.sqrt(randomArea / Math.PI + r1 * r1);
  
    // 生成一个 [0, 2 * Math.PI) 之间的随机角度
    const randomAngle = Math.random() * 2 * Math.PI;
  
    // 计算点的 x 和 y 坐标
    const x = radius * Math.cos(randomAngle);
    const y = radius * Math.sin(randomAngle);
  
    return [x, y];
}

export function getRandomPointOnSphere(x: number, y: number, z: number, r: number): [number, number, number] {
    // 生成均匀分布的随机方向向量
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);

    // 计算球面上的坐标
    const x1 = x + r * Math.sin(phi) * Math.cos(theta);
    const y1 = y + r * Math.sin(phi) * Math.sin(theta);
    const z1 = z + r * Math.cos(phi);

    return [x1, y1, z1];
}

export function getUniformlyDistributedPointsOnSphere(
    x: number,
    y: number,
    z: number,
    r: number,
    pointCount: number = 20
): [number, number, number][] {
    const points: [number, number, number][] = [];

    // 使用球面纳维尔-斯托克斯方法生成均匀分布的点
    const goldenRatio = (1 + Math.sqrt(5)) / 2;
    for (let i = 0; i < pointCount; i++) {
        const t = 2 * i + 1; // 均匀分布的角度
        const inclination = Math.acos(1 - (2 * t) / pointCount);
        const azimuth = 2 * Math.PI * i * goldenRatio;

        // 检查计算结果是否有效
        if (
            isFinite(inclination) &&
            isFinite(azimuth) &&
            isFinite(x + r * Math.sin(inclination) * Math.cos(azimuth)) &&
            isFinite(y + r * Math.sin(inclination) * Math.sin(azimuth)) &&
            isFinite(z + r * Math.cos(inclination))
        ) {
            const x1 = x + r * Math.sin(inclination) * Math.cos(azimuth);
            const y1 = y + r * Math.sin(inclination) * Math.sin(azimuth);
            const z1 = z + r * Math.cos(inclination);

            points.push([x1, y1, z1]);
        }
    }

    return points;
}

/**
 * 创建震荡函数
 */
export function createOscillation(low: number, high: number, delta: number, switchCb?: Function) {
    if (low >= high) throw new Error('低点大于高点');
    let isIncrease = true;
    let progress = low;
    return (...args) => {
        if (isIncrease) {
            progress += delta;
            if (progress >= high) {
                isIncrease = false;
                progress = high;
                switchCb?.(args);
            }
        } else {
            progress -= delta;
            if (progress <= low) {
                isIncrease = true;
                progress = low;
                switchCb?.(args);
            }
        }
        return { progress, isIncrease };
    }
}

/**
 * 创建进度函数
 */
export function createProgress(duration: number) {
    let initTimestamp = 0;
    return function update(timestamp: number) {
        if (!initTimestamp) initTimestamp = timestamp;
        const passTime = timestamp - initTimestamp;
        let progress = passTime / duration;
        if (progress > 1) progress = 1;
        return progress;
    }
}

export function lerp(start: number, end: number, progress: number) {
    return start + (end - start) * progress;
}

/**
 * 判断 token 有没有超过时间
 */
export function hasExceededTimes(timestampMs: number, time: number = 1 * 60 * 60 * 1000): boolean {
    // 获取当前时间的 Unix 时间戳（毫秒）
    const currentTimeMs = Date.now();
    
    // 判断当前时间是否已经超过指定时间戳两个小时
    return currentTimeMs > (timestampMs + time);
}